import { useState } from "react";
import type { Contact, Lead } from "@lumika/shared";
import { ChevronDown, MessageSquarePlus, Plus, Target } from "lucide-react";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@lumika/ui";
import { ComposeDialog } from "./ComposeDialog";
import { NewLeadDialog } from "./NewLeadDialog";

type ConversationTarget = {
  contact: Contact;
  leadId: string;
  rooftopId: string | null;
};

/**
 * Stable global creation menu. A Lead is an opportunity and a Conversation
 * is outreach against one; exposing both labels before anything opens keeps
 * the shell from implying that creating one silently creates the other.
 */
export function NewMenu() {
  const [conversationOpen, setConversationOpen] = useState(false);
  const [newLeadOpen, setNewLeadOpen] = useState(false);
  const [conversationTarget, setConversationTarget] =
    useState<ConversationTarget>();

  function startConversationForLead(contact: Contact, lead: Lead) {
    setConversationTarget({
      contact,
      leadId: lead.id,
      rooftopId: lead.rooftopId,
    });
    setConversationOpen(true);
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button size="sm" aria-label="Create new">
            <Plus className="h-4 w-4" />
            <span>New</span>
            <ChevronDown className="h-3.5 w-3.5 opacity-70" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuItem
            className="min-h-touch items-start py-2"
            onSelect={() => {
              setConversationTarget(undefined);
              setConversationOpen(true);
            }}
          >
            <MessageSquarePlus className="mt-0.5 h-4 w-4 shrink-0" />
            <span>
              <span className="block font-medium">Start conversation</span>
              <span className="block text-xs text-muted-foreground">
                Message or call a contact
              </span>
            </span>
          </DropdownMenuItem>
          <DropdownMenuItem
            className="min-h-touch items-start py-2"
            onSelect={() => setNewLeadOpen(true)}
          >
            <Target className="mt-0.5 h-4 w-4 shrink-0" />
            <span>
              <span className="block font-medium">New lead</span>
              <span className="block text-xs text-muted-foreground">
                Create an opportunity
              </span>
            </span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <NewLeadDialog
        open={newLeadOpen}
        onOpenChange={setNewLeadOpen}
        onStartConversation={startConversationForLead}
      />
      <ComposeDialog
        key={conversationTarget?.leadId ?? "new-conversation"}
        presetContact={conversationTarget?.contact}
        targetLeadId={conversationTarget?.leadId}
        targetRooftopId={conversationTarget?.rooftopId}
        open={conversationOpen}
        onOpenChange={(open) => {
          setConversationOpen(open);
          if (!open) setConversationTarget(undefined);
        }}
        trigger="none"
      />
    </>
  );
}
